import { LoaderCircle } from "lucide-react";
import { type ReactNode, useEffect, useState } from "react";

import { MARKETPLACE_THUMBNAIL_RENDER_VERSION } from "../../shared/marketplace-media-version";
import { inscriptionMediaFailureAction, type InscriptionMediaSource } from "../lib/inscription-media";
import { InscriptionDocumentFrame } from "./InscriptionDocumentFrame";
import { PixelArtImage } from "./PixelArtImage";
import { apiResourceUrl } from "../lib/api-fetch";

function imageUrl(id: string): string {
  return apiResourceUrl(`/api/marketplace/image/${encodeURIComponent(id)}`);
}

function thumbnailUrl(id: string, attempt: number): string {
  const retry = attempt ? `&retry=${attempt}` : "";
  return apiResourceUrl(`/api/marketplace/thumbnail/${encodeURIComponent(id)}?v=${MARKETPLACE_THUMBNAIL_RENDER_VERSION}${retry}`);
}

export function ResilientInscriptionMedia({
  inscriptionId,
  label,
  className,
  priority = false,
  children,
}: {
  inscriptionId: string;
  label: string;
  className: string;
  priority?: boolean;
  children?: ReactNode;
}) {
  const [source, setSource] = useState<InscriptionMediaSource>("thumbnail");
  const [attempt, setAttempt] = useState(0);
  const [preparing, setPreparing] = useState(false);

  useEffect(() => {
    setSource("thumbnail");
    setAttempt(0);
    setPreparing(false);
  }, [inscriptionId]);

  useEffect(() => {
    if (!preparing || source !== "thumbnail") return;
    const delay = Math.min(1_500 * 2 ** attempt, 6_000);
    const timeout = window.setTimeout(() => {
      setAttempt((current) => current + 1);
      setPreparing(false);
    }, delay);
    return () => window.clearTimeout(timeout);
  }, [attempt, preparing, source]);

  const classes = [
    className,
    preparing ? "preparing" : "",
    source === "document" ? "document" : "",
  ].filter(Boolean).join(" ");

  return <span className={classes}>
    {children}
    {preparing ? <span className="inscription-media-status"><LoaderCircle className="spin" /><small>PREPARING</small></span> : null}
    {source === "document" ? <InscriptionDocumentFrame
      source={{ inscriptionId }}
      title={label}
      loading={priority ? "eager" : "lazy"}
    /> : <PixelArtImage
      key={`${inscriptionId}:${source}:${attempt}`}
      src={source === "thumbnail"
        ? thumbnailUrl(inscriptionId, attempt)
        : source === "external-image"
          ? `https://ordinals.com/content/${encodeURIComponent(inscriptionId)}`
          : imageUrl(inscriptionId)}
      alt={label}
      referrerPolicy="no-referrer"
      loading={priority ? "eager" : "lazy"}
      decoding="async"
      fetchPriority={priority ? "high" : "auto"}
      onLoad={() => {
        setPreparing(false);
      }}
      onError={() => {
        const action = inscriptionMediaFailureAction(source, attempt);
        if (action === "retry") {
          setPreparing(true);
          return;
        }
        setPreparing(false);
        setSource(action);
      }}
    />}
  </span>;
}
